import { Component, inject, input, output } from '@angular/core';
import { TypographyComponent } from '@purolator/ui-kit-angular';
import { TranslatePipe, TranslationService } from '../../../../core/i18n';
import {
  formatReviewAddressBlock,
  type ReviewAddressView,
  type ReviewReturnAddressView,
} from '../../utils/map-review-address';
import { ReviewDetailCard } from '../review-detail-card/review-detail-card';

/** Which address card to render. */
export type ReviewAddressCardKind = 'shipFrom' | 'shipTo' | 'returnTo';

/**
 * Ship From, Ship To, or Return To Address summary card on Review & Pay.
 */
@Component({
  selector: 'app-review-address-summary',
  imports: [ReviewDetailCard, TranslatePipe, TypographyComponent],
  templateUrl: './review-address-summary.html',
  styleUrl: './review-address-summary.scss',
})
export class ReviewAddressSummary {
  private readonly translation = inject(TranslationService);

  readonly kind = input.required<ReviewAddressCardKind>();
  readonly address = input<ReviewAddressView | null>(null);
  readonly returnAddress = input<ReviewReturnAddressView | null>(null);
  /** When false, hides Edit (Get Labels confirmation). */
  readonly showEdit = input(true);
  readonly editRequested = output<string>();

  /**
   * Resolves the Figma card title i18n key for this address kind.
   */
  protected titleKey(): string {
    return `createShipment.reviewAndPay.cards.${this.kind()}`;
  }

  /** Address view for ship-from, ship-to, or a distinct return address. */
  protected activeAddressView(): ReviewAddressView | null {
    if (this.kind() === 'returnTo') {
      return this.returnAddress()?.address ?? null;
    }

    return this.address();
  }

  /**
   * Compact address lines (name, street, suite, entry code, locality) for one typography block.
   */
  protected addressBlockLines(view: ReviewAddressView): string[] {
    return formatReviewAddressBlock(view, {
      suiteAptLabel: this.translation.translate('createShipment.reviewAndPay.suiteAptLabel'),
      buzzerLabel: this.translation.translate('createShipment.reviewAndPay.buzzerLabel'),
    });
  }
}
